import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOperatorMe } from "@/hooks/useOperatorMe";
import { buildCanonicalHost } from "@/lib/tenantFromHost";
import { Button } from "@/components/ui/button";
import { StaffOrgSwitcher } from "@/components/StaffOrgSwitcher";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();

  // Operator-login: the org comes from the operator's membership, not the host.
  // We fetch it to (a) route the operator to their org's subdomain and (b) show
  // a no-org state instead of another tenant's branding.
  //
  // Shared with AppShell (which wraps the whole route table and therefore
  // mounts first) and with OperatorHomeRedirect. One query key, one staleTime,
  // so whichever mounts first fetches and the rest read that cache entry.
  const meQuery = useOperatorMe();

  // While auth resolves, render nothing rather than a centered spinner.
  // Cached sessions resolve in tens of ms — a blank frame is imperceptible.
  if (loading) return null;

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // Force a password change for accounts flagged by an admin reset.
  const mustReset = (user.user_metadata as Record<string, unknown> | null)?.must_reset_password === true;
  if (mustReset && location.pathname !== "/reset-password") {
    return <Navigate to="/reset-password" replace />;
  }

  // Wait for membership before deciding where the operator belongs.
  if (meQuery.isLoading) return null;

  if (meQuery.isError) {
    return (
      <OperatorGate
        title="Couldn’t load your account"
        body="We couldn’t determine your organization right now. Sign out and back in, or contact your DigaVouch administrator if this persists."
        onSignOut={signOut}
      />
    );
  }

  const me = meQuery.data;
  const currentHost =
    typeof window !== "undefined" ? window.location.hostname.toLowerCase().replace(/:\d+$/, "") : "";
  // Org with a subdomain → that subdomain; no org (or org without a subdomain)
  // → the neutral `app` host.
  const desiredLabel = me?.org?.subdomain || "app";
  const targetHost = buildCanonicalHost(currentHost, desiredLabel);

  // targetHost is null on non-canonical hosts (localhost / *.netlify.app /
  // previews) — skip org routing entirely there so dev + demo keep working.
  if (targetHost) {
    if (targetHost !== currentHost) {
      // Send the operator to their org's host (or app when unassigned),
      // preserving the path. Full navigation so host-based branding reloads.
      window.location.assign(`https://${targetHost}${location.pathname}${location.search}`);
      return null;
    }
    if (!me?.org) {
      // DigaVouch staff with no membership of their own: nothing to land on
      // until they pick an org to view. The server said they are staff.
      if (me?.staff) {
        return (
          <OperatorGate
            title="Choose an organization to view"
            body="Your account has no organization of its own. Pick the one you want to view as DigaVouch staff."
            onSignOut={signOut}
          >
            <StaffOrgSwitcher className="mt-4 items-center" />
          </OperatorGate>
        );
      }
      return (
        <OperatorGate
          title="No organization assigned"
          body="Your account isn’t assigned to an organization yet. Contact your DigaVouch administrator to get access."
          onSignOut={signOut}
        />
      );
    }
  }

  return <>{children}</>;
}

function OperatorGate({
  title,
  body,
  onSignOut,
  children,
}: {
  title: string;
  body: string;
  onSignOut: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-slate-800">{title}</h1>
        <p className="mt-2 text-sm text-slate-600">{body}</p>
        {children}
        <Button variant="outline" className="mt-5" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
